import { Box, Flex, FlexProps, Image } from '@chakra-ui/react'
import { DateOrTime, Grid, Icon, IconButton, Panel, Text, TextProps } from 'components'
import { useEffect, useRef, useState } from 'react'
import { IMedia } from 'types'
import { useLocalStorage } from 'usehooks-ts'
import {
  getImageLightness,
  getImagePath,
  getThumbPath,
  useBreakpoint,
  useDisclosure,
  useMedia,
  usePermission,
  useStorageUrl,
  useUser,
} from 'utils'
import { PdfIcon } from '../icons'
import { useCurrentUser } from '../providers'
import MediaDialog from './MediaDialog'

export interface Props extends FlexProps {
  hideTitle?: boolean
  id: string
  onDelete?: () => Promise<boolean>
  onChoose?: () => void
}

export default function MediaTile({ hideTitle, id, onChoose, onDelete, ...rest }: Props) {
  const disclosure = useDisclosure()
  const user = useCurrentUser()
  const isMobile = useBreakpoint() < 2
  const { media } = useMedia(id)
  const { updatedBy, type, title, updated } = media || {}
  const updatedUser = useUser(updatedBy)
  const permission = usePermission()

  const handleDelete = user?.isAdmin || updatedUser === user?.id ? onDelete : undefined
  const hasOpened = useRef(false)
  const [view] = useLocalStorage<string>('content-display-mode', 'grid')
  const listView = view === 'list'

  function handleClick() {
    if (onChoose) return onChoose()
    hasOpened.current = true
    disclosure.onOpen()
  }

  if (listView) {
    return (
      <Panel cursor="pointer" p={isMobile ? 2 : 3} onClick={handleClick}>
        <Grid templateColumns={isMobile ? '40% 25% 25%' : '65% 20% 15%'} gap={3}>
          <Flex align="center" gap={3}>
            {!media ? null : type === 'pdf' ? (
              <PDFThumb isListView />
            ) : (
              <ImageThumb isListView media={media} />
            )}

            {!hideTitle && title && <Text textStyle="bodySmall">{title}</Text>}
          </Flex>
          {updatedUser && <Text {...imageMetaTextProps}>{updatedUser.name}</Text>}
          {updated && <DateOrTime {...imageMetaTextProps}>{updated}</DateOrTime>}
        </Grid>

        {media && hasOpened.current && (
          <MediaDialog data={media} onDelete={handleDelete} {...disclosure} />
        )}
      </Panel>
    )
  }

  return (
    <Flex position="relative" cursor="pointer" onClick={handleClick} direction="column" {...rest}>
      {!media ? null : type === 'pdf' ? <PDFThumb /> : <ImageThumb media={media} />}

      {!hideTitle && title && (
        <Box p={2} px={3} borderBottomRadius={8} backgroundColor="#FFFFFF">
          <Text textStyle="bodySmall" fontWeight="normal" truncate color="black">
            {title}
          </Text>
        </Box>
      )}

      {permission.content !== 'viewer' && (
        <Flex position="absolute" top={2} right={2} backgroundColor="#FFFFFF66" borderRadius={8}>
          {onChoose && (
            <IconButton
              aria-label="preview-media"
              color="slateblue"
              size="sm"
              variant="ghost"
              onClick={e => {
                disclosure.onOpen()
                e.stopPropagation()
              }}
              icon={<Icon>preview</Icon>}
            />
          )}
        </Flex>
      )}

      {media && hasOpened.current && (
        <MediaDialog data={media} onDelete={handleDelete} {...disclosure} />
      )}
    </Flex>
  )
}

function ImageThumb({ media, isListView }: { media: IMedia; isListView?: boolean }) {
  const { id, type } = media
  const { url } = useStorageUrl(getThumbPath(id), getImagePath(id))

  return (
    <>
      <Image
        height={isListView ? '50px' : '146px'}
        w={isListView ? '50px' : '100%'}
        objectFit="cover"
        borderTopRadius={8}
        borderBottomRadius={isListView ? 8 : 0}
        draggable={false}
        src={url}
      />

      {type === 'video' ? (
        isListView ? (
          <Flex position="absolute" left="20px" top="20px">
            <PlayIconThumb url={url} />
          </Flex>
        ) : (
          <Flex position="absolute" align="center" justify="center" inset={0}>
            <PlayIconThumb url={url} />
          </Flex>
        )
      ) : null}
    </>
  )
}

function PlayIconThumb({ url }: { url?: string }) {
  const [brightness, setBrightness] = useState(100)

  useEffect(() => {
    if (url) getImageLightness(url).then(setBrightness)
  }, [url])

  return (
    <Icon size="lg" color={brightness < 0.5 ? 'white' : 'black'}>
      play_circle
    </Icon>
  )
}

function PDFThumb({ isListView }: { isListView?: boolean }) {
  return (
    <Flex
      bg="white"
      height={isListView ? '50px' : '146px'}
      w={isListView ? '50px' : '100%'}
      objectFit="cover"
      borderTopRadius={8}
      borderBottomRadius={isListView ? 8 : 0}
      draggable={false}
      align="center"
      justify="center"
    >
      <PdfIcon height="50%" width="50%" />
    </Flex>
  )
}

const imageMetaTextProps: TextProps = {
  textStyle: 'bodySmall',
  fontWeight: 'normal',
  alignContent: 'center',
}
